import { User, OvertimeRequest } from '@/types';
import { supabase } from "@/integrations/supabase/client";
import { OvertimeStatus } from "@/types/enums";

// Function to get company settings for max overtime hours
export async function getMaxOvertimeHours(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('max_overtime_hours')
      .eq('is_deleted', false)
      .limit(1);
    
    if (error) {
      return 104; // Default fallback
    }
    
    return data && data.length > 0 && data[0] && 'max_overtime_hours' in data[0]
      ? (data[0] as any).max_overtime_hours 
      : 104; // Default fallback
  } catch {
    return 104; // Default fallback
  }
}

// Function to check if an overtime request exceeds the company's monthly limit
export async function checkOvertimeMonthlyLimit(
  user: User,
  approvedRequests: OvertimeRequest[],
  requestedHours: number,
  currentDate: Date = new Date(),
  requestDate?: Date
) {
  // Get the company's configured max overtime hours
  const maxAllowedHours = await getMaxOvertimeHours();
  
  // Default to current month if no request date is provided
  const targetDate = requestDate || currentDate;
  const targetMonth = targetDate.getMonth();
  const targetYear = targetDate.getFullYear();
  
  // Filter approved requests for the current month and this employee
  const employeeApprovedRequestsThisMonth = approvedRequests.filter(req => {
    const reqDate = new Date(req.date);
    return (
      req.employeeId === user.id && 
      req.status === OvertimeStatus.APPROVED &&
      reqDate.getMonth() === targetMonth &&
      reqDate.getFullYear() === targetYear
    );
  });
  
  // Calculate total approved hours for this month
  const currentMonthHours = employeeApprovedRequestsThisMonth.reduce(
    (total, req) => total + req.hours, 
    0
  );
  
  // Calculate total with the new request
  const totalHoursWithRequested = currentMonthHours + requestedHours;
  
  // Check if the total exceeds the limit
  const requiresVPApproval = totalHoursWithRequested > maxAllowedHours;
  
  return {
    requiresVPApproval,
    currentMonthHours,
    totalHoursWithRequested,
    maxAllowedHours
  };
}

// This function is used in eligibilityUtils.test.ts
export const checkOvertime104HourLimit = checkOvertimeMonthlyLimit;

// Function to check employee eligibility for overtime
export async function checkEmployeeOvertimeEligibility(
  employee: User | undefined,
  employeeId: string,
  hours: number,
  overtimeRequests: OvertimeRequest[]
): Promise<{ 
  eligible: boolean; 
  reason?: string; 
  requiresVPApproval?: boolean;
  currentMonthlyHours?: number;
  maxAllowedHours?: number;
}> {
  // Check if employee exists
  if (!employee) {
    return { eligible: false, reason: 'Employee not found' };
  }

  // Check employee active status if available
  if (employee.isActive === false) {
    return { eligible: false, reason: 'Employee is not active' };
  }

  // Get current date
  const currentDate = new Date();
  
  // Check monthly limit
  const overtimeCheck = await checkOvertimeMonthlyLimit(
    employee,
    overtimeRequests,
    hours,
    currentDate
  );
  
  // Return final eligibility result
  return {
    eligible: true,
    requiresVPApproval: overtimeCheck.requiresVPApproval,
    currentMonthlyHours: overtimeCheck.currentMonthHours,
    maxAllowedHours: overtimeCheck.maxAllowedHours
  };
}
